import {
  IconLabelToggle,
  Menu,
  MenuOption,
  Search,
  SecondaryButton,
  SquareDotLoadingIndicator,
  Trigger,
  typography,
} from '@joor/design-system'
import styled from 'styled-components/macro'

import { ActionBarIds } from '../../joorPayOrders.ids'
import { messages } from 'features/Orders/ListView/constants/messages'

export function ActionsBar({
  onSearch,
  isDataEmpty,
  areRowsSelected,
  isJoorPayEnabled,
  isFilterResultsEmpty,
  onSendInvoiceClick,
  onChangeStatusClick,
  onExportClick,
  searchText = '',
  totalOrders,
  ordersCount = 0,
  hasError = false,
  testId,
  filtersMenuComponent,
}: ActionsBarProps) {
  const isDataLoading = ordersCount < totalOrders
  const disabled = Boolean(
    isDataLoading || isDataEmpty || !isJoorPayEnabled || hasError,
  )
  const disableActionsMenu = Boolean(disabled || !areRowsSelected)
  const disableExportBtn = Boolean(isFilterResultsEmpty || disabled)

  const showLoading = isDataLoading && !hasError
  const showLoadedText = !isDataLoading && !hasError

  return (
    <Container data-testid={testId}>
      <LeftActions>
        <Search
          onSearch={({ searchText }) => onSearch(searchText)}
          incrementalSearch
          placeholder={messages.search}
          disabled={disabled}
          value={searchText}
          id={ActionBarIds.SearchBar}
        />
        {showLoading && (
          <LoadingContainer>
            <LoaderStyled testId={ActionsBarTestIds.Loader} />
            <Text>{messages.loadingOfTotal(ordersCount, totalOrders)}</Text>
          </LoadingContainer>
        )}
        {showLoadedText && (
          <Text className="margin">{messages.totalOrders(totalOrders)}</Text>
        )}
      </LeftActions>
      <RightActions>
        <Menu
          id={ActionBarIds.ActionsMenu}
          disabled={disableActionsMenu}
          autoClose
          testId={ActionsBarTestIds.ActionMenuWrapper}
          options={[
            {
              content: (
                <MenuOption
                  testId={ActionsBarTestIds.SendPaymentLinkOption}
                  iconName="plane"
                  onClick={onSendInvoiceClick}
                >
                  {messages.sendPaymentLink}
                </MenuOption>
              ),
            },
            {
              content: (
                <MenuOption
                  testId={ActionsBarTestIds.ChangeOrderStatusOption}
                  iconName="sync"
                  onClick={onChangeStatusClick}
                >
                  {messages.changeOrderStatus}
                </MenuOption>
              ),
            },
          ]}
          position="left"
          trigger={
            <Trigger
              iconName="layer"
              label={messages.actionsLabel}
              testId={ActionsBarTestIds.ActionsMenu}
            />
          }
        />
        {filtersMenuComponent}
        <IconLabelToggle
          id={ActionBarIds.ExportTableButton}
          iconName="download"
          label={messages.exportTableLabel}
          onToggle={() => onExportClick(areRowsSelected)}
          variant={disableExportBtn ? 'DISABLED' : 'DEFAULT'}
          checked={false}
        />
        <SecondaryButton
          iconName="link-out"
          className="notranslate"
          onClick={() =>
            window.open('https://admin.rch.io/', '_blank', 'noreferrer')
          }
          disabled={!isJoorPayEnabled}
        >
          {messages.reachDashboard}
        </SecondaryButton>
      </RightActions>
    </Container>
  )
}

type ActionsBarProps = {
  onSearch: (value: string) => void
  onSendInvoiceClick: () => void
  onChangeStatusClick: () => void
  onExportClick: (areRowsSelected?: boolean) => void
  filtersMenuComponent: JSX.Element
  areRowsSelected?: boolean
  isJoorPayEnabled?: boolean
  isFilterResultsEmpty?: boolean
  hasError?: boolean
  isDataEmpty?: boolean
  searchText?: string
  testId?: string
  totalOrders: number
  ordersCount?: number
}

export const ActionsBarTestIds = {
  ActionsMenu: 'actions-menu',
  ActionMenuWrapper: 'action-menu-wrapper',
  SendPaymentLinkOption: 'send-payment-link-option',
  ChangeOrderStatusOption: 'change-order-status-option',
  Loader: 'actions-loader',
} as const

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
`

const LeftActions = styled.div`
  display: flex;
  align-items: center;
`

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`
const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
`

const LoaderStyled = styled(SquareDotLoadingIndicator)`
  transform: scale(0.6);
  > div {
    margin-top: 0 !important;
  }
`

const Text = styled.p`
  ${typography.alpha.primaryActive.body3}
  color: var(--color-primary-400);

  &.margin {
    margin-left: 12px;
  }
`
